/** Copyright (c) 2019 T. Rowe Price. All rights reserved. */

import './MailingAddress.scss';

import { bool, func, object } from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';

import { ReactComponent as WarningCircleIcon } from 'assets/icons/warning-circle.svg';
import { InvalidMailingAddresses } from 'components/layouts/ContactMethodMaintenance/MailingAddresses';
import AddressTypeSelect from 'components/layouts/Preferences/UpdateContactInformation/MailingAddress/AddressTypeSelect';
import MilitaryAddress from 'components/layouts/Preferences/UpdateContactInformation/MailingAddress/MilitaryAddress';
import NonUSAddress from 'components/layouts/Preferences/UpdateContactInformation/MailingAddress/NonUSAddress';
import PreferencesDataRow from 'components/layouts/Preferences/shared/PreferencesDataRow';
import PreferencesDataRowLabel from 'components/layouts/Preferences/shared/PreferencesDataRowLabel';
import PreferencesDataRowValue from 'components/layouts/Preferences/shared/PreferencesDataRowValue';
import PreferencesCol from 'components/layouts/Preferences/shared/PreferencesCol';
import PreferencesRow from 'components/layouts/Preferences/shared/PreferencesRow';
import PreferencesRowLabel from 'components/layouts/Preferences/shared/PreferencesRowLabel';
import USAddress from 'components/layouts/Preferences/UpdateContactInformation/MailingAddress/USAddress';
import BaseModalButton from 'components/shared/Modal/BaseModalButton';
import Content from 'services/Content';
import {
  getFilteredContactMethodMaintenance,
  getHasInvalidMailingAddressesOnly
} from 'state/contactMethodMaintenance/ContactMethodMaintenanceSelectors';
import { contactInfoAddressValidation, contactInfoSecondaryAddressValidation } from 'services/Validation';
import DisclosureModal from 'components/shared/Disclosure/DisclosureModal';
import { ReactComponent as QuestionCircle } from 'assets/icons/question-circle.svg';
import { hasResidentialAddress } from 'state/customer/CustomerSelectors';
import Checkbox from 'components/shared/Checkbox/Checkbox';
import { getWarningTextContent } from 'components/layouts/Preferences/UpdateContactInformation/PreferencesUpdateContactInformation.js';

const MailingAddress = ({
  address = {},
  contactMethodMaintenanceFiltered,
  hasInvalidMailingAddress,
  onChange,
  hasResidential,
  isMailingSameAsResidential,
  onSameAsSelected,
  owns529Only,
  ownsBrokerage
}) => {
  const { addressType } = address;
  let AddressComponent = USAddress;
  if (addressType === 'military') {
    AddressComponent = MilitaryAddress;
  } else if (addressType === 'non-us') {
    AddressComponent = NonUSAddress;
  }

  const onAddressChange = (field, value) => onChange(hasResidential ? 'mailingAddress' : 'address', field)(value);

  const warningTextContent = getWarningTextContent(addressType, owns529Only, ownsBrokerage);
  return (
    <PreferencesRow className="mailing-address-section">
      <PreferencesCol className="mailing-address-title" col={3} mobile={12}>
        <div>
          <div className="mailing-address">
            <PreferencesRowLabel label="Mailing Address" />
            {hasResidential && (
              <DisclosureModal
                className="restricted-height-modal"
                title="Mailing Address"
                icon={<QuestionCircle className="information-modal-icon" />}
                content={Content.mailingAddressToolTipCopy}
                hideLinkContent
              />
            )}
          </div>
          {hasResidential && (
            <div
              className="mailing-address-copy-label"
              data-testid="mailing-address-copy-label">
              <span
                dangerouslySetInnerHTML={{ __html: Content.mailingAddressCopy }}
              />
            </div>
          )}
        </div>
        <BaseModalButton
          buttonClassName="question-icon"
          label={<WarningCircleIcon />}
          title="Mailing Address">
          <span
            className="warning-text"
            dangerouslySetInnerHTML={{ __html: warningTextContent }}
          />
        </BaseModalButton>
      </PreferencesCol>
      <PreferencesCol
        col={6}
        mobile={12}
        className="editable-preferences-section">
        {hasInvalidMailingAddress && (
          <PreferencesDataRow>
            <PreferencesDataRowValue className="address-warning-row">
              <InvalidMailingAddresses
                address={contactMethodMaintenanceFiltered.mailingAddresses}
                addressLabel="Mailing Address"
              />
            </PreferencesDataRowValue>
          </PreferencesDataRow>
        )}
        {hasResidential && (
          <PreferencesDataRow className="mailing-address-checkbox">
            <Checkbox
              checked={isMailingSameAsResidential}
              className="profile-mailing-checkbox"
              dataTestId="profile-mailing-sameas-checkbox"
              name={'mailing-address-checkbox'}
              label={'Same as residential/legal address'}
              disabled={false}
              onChange={onSameAsSelected}
            />
          </PreferencesDataRow>
        )}
        {!isMailingSameAsResidential && (
          <React.Fragment>
            <PreferencesDataRow className="address-type-select">
              <PreferencesDataRowLabel>Address Type</PreferencesDataRowLabel>
              <PreferencesDataRowValue>
                <AddressTypeSelect
                  onChange={onAddressChange}
                  selected={addressType}
                  mailingType="mailing"
                />
              </PreferencesDataRowValue>
            </PreferencesDataRow>
            <AddressComponent
              address={address}
              onChange={onAddressChange}
              mailingType="mailing"
              hasResidentialAddress={hasResidential}
              validations={
                hasResidential
                  ? contactInfoSecondaryAddressValidation
                  : contactInfoAddressValidation
              }
            />
          </React.Fragment>
        )}
      </PreferencesCol>
      <PreferencesCol col={3} mobile={12}>
        <div className="warning">
          <WarningCircleIcon className="warning-icon" />
          <span
            className="warning-text"
            dangerouslySetInnerHTML={{ __html: warningTextContent }}
          />
        </div>
      </PreferencesCol>
    </PreferencesRow>
  );
};

MailingAddress.propTypes = {
  address: object.isRequired,
  hasInvalidMailingAddress: bool.isRequired,
  onChange: func.isRequired
};

const mapStateToProps = (state) => ({
  contactMethodMaintenanceFiltered: getFilteredContactMethodMaintenance(state),
  hasInvalidMailingAddress: getHasInvalidMailingAddressesOnly(state),
  hasResidential: hasResidentialAddress(state),
});

export default connect(mapStateToProps)(MailingAddress);
